import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Loader2 } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminRedemptionInstructions = () => {
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('en');

  useEffect(() => {
    fetchInstructions();
  }, []);

  const fetchInstructions = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/site-content/redemption-instructions`);
      setInstructions(response.data || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching instructions:', error);
      setError('Failed to load redemption instructions');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (index, lang, value) => {
    const updated = [...instructions];
    if (!updated[index].value) {
      updated[index].value = { en: '', ru: '', kz: '' };
    }
    updated[index].value[lang] = value;
    setInstructions(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage('');
    setError(null);

    try {
      // Update each instruction individually
      for (const instruction of instructions) {
        await axios.put(`${API}/api/admin/site-content/${instruction.id}`, {
          value: instruction.value
        });
      }
      
      setSuccessMessage('✅ Redemption instructions updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving instructions:', error);
      setError('Failed to save instructions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-black">Redemption Instructions</h1>
            <p className="text-gray-600 mt-2">
              Edit the 7-step instructions displayed on gift card customization pages
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {successMessage}
          </div>
        )}

        {/* Language Tabs */}
        <div className="mb-6">
          <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('en')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'en'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              English
            </button>
            <button
              onClick={() => setActiveTab('ru')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'ru'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Russian
            </button>
            <button
              onClick={() => setActiveTab('kz')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'kz'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Kazakh
            </button>
          </div>
        </div>

        {/* Instructions List */}
        <div className="space-y-6">
          {instructions.map((instruction, index) => (
            <div key={instruction.id} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 font-bold rounded-full">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Step {index + 1}
                </h3>
              </div>
              
              <textarea
                value={instruction.value?.[activeTab] || ''}
                onChange={(e) => handleUpdate(index, activeTab, e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows="2"
                placeholder={`Enter instruction ${index + 1} in ${activeTab === 'en' ? 'English' : activeTab === 'ru' ? 'Russian' : 'Kazakh'}`}
              />
              
              <div className="mt-2 text-sm text-gray-500">
                {instruction.value?.[activeTab]?.length || 0} characters
              </div>
            </div>
          ))}
        </div>

        {/* Help Section */}
        <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">💡 Tips for Writing Instructions</h3>
          <ul className="space-y-2 text-blue-800">
            <li className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span>Keep each step clear and concise (aim for 1-2 sentences)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span>Include specific URLs or button names when relevant</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span>Ensure all three languages (EN, RU, KZ) are filled in for consistency</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600">•</span>
              <span>Changes will appear immediately on the gift card customization page</span>
            </li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminRedemptionInstructions;
